import { createReducer, on } from '@ngrx/store';

import { DevicesActions } from './devices.actions';
import { deviceAdapter, DevicesState, RichDevice } from './devices.state';

export const initialDevicesState: DevicesState = {
  devices: deviceAdapter.getInitialState(),
  featuresWithPendingUpdates: [],
  selectedDeviceId: null,
};

export const devicesReducer = createReducer(
  initialDevicesState,

  on(
    DevicesActions.loadDevicesSuccess,
    (state, { devices }): DevicesState => {
      const _devices = devices.map((dev: RichDevice) => {
        dev.isSelected = dev.selectId === state.selectedDeviceId;

        return dev;
      });

      return {
        ...state,
        devices: deviceAdapter.setAll(_devices, state.devices),
      };
    }
  ),

  on(
    DevicesActions.setSelectedDevice,
    DevicesActions.setSelectedDeviceOnUserClick,
    (state, { gatewaySerial, deviceId, selectId }): DevicesState => {
      const selectableDevices: RichDevice[] = Object.values(state.devices.entities);

      const deviceToSelect = selectableDevices.find(
        (device) =>
          device.gatewaySerial === gatewaySerial &&
          device.id === deviceId &&
          (selectId ? device.selectId === selectId : true)
      );
      const newSelectId: string = deviceToSelect?.selectId;
      state.selectedDeviceId = newSelectId;
      state = updateIsSelectedOfDevices(state, newSelectId);

      return state;
    }
  ),

  on(
    DevicesActions.resetSelectedDeviceId,
    (state): DevicesState => {
      state.selectedDeviceId = null;
      state = updateIsSelectedOfDevices(state, null);

      return state;
    }
  ),
  on(
    DevicesActions.setSelectedDeviceWithoutFetchingFeatures,
    (state, { deviceSelectId }): DevicesState => {
      state.selectedDeviceId = deviceSelectId;
      state = updateIsSelectedOfDevices(state, deviceSelectId);

      return state;
    }
  ),
  on(
    DevicesActions.setSelectedDeviceById,
    (state, { deviceSelectId }): DevicesState => {
      state.selectedDeviceId = deviceSelectId;
      state = updateIsSelectedOfDevices(state, deviceSelectId);

      return state;
    }
  ),
  on(
    DevicesActions.switchSelectionToMainDevice,
    (state, {}): DevicesState => {
      const selectedGatewaySerial: string =
        state.devices.entities[state.selectedDeviceId]?.gatewaySerial;
      const deviceToSelect: RichDevice = Object.values(state.devices.entities).find(
        (device) =>
          device.gatewaySerial === selectedGatewaySerial &&
          (device.isMainDeviceOfGateway || device.isStandalone)
      );

      const deviceSelectId: string = deviceToSelect?.selectId;
      state.selectedDeviceId = deviceSelectId;
      state = updateIsSelectedOfDevices(state, deviceSelectId);

      return state;
    }
  ),

  on(DevicesActions.updateBmuSerial, (state) => {
    return {
      ...state,
      devices: deviceAdapter.updateOne(
        {
          id: state.selectedDeviceId,
          changes: {
            loading: true,
          },
        },
        state.devices
      ),
    };
  }),

  on(DevicesActions.updateBmuSerialSuccess, (state, { bmuSerial }) => {
    return {
      ...state,
      devices: deviceAdapter.updateOne(
        {
          id: state.selectedDeviceId,
          changes: {
            loading: false,
            bmuSerial,
          },
        },
        state.devices
      ),
    };
  }),

  on(DevicesActions.updateBmuSerialError, (state) => {
    return {
      ...state,
      devices: deviceAdapter.updateOne(
        {
          id: state.selectedDeviceId,
          changes: {
            loading: false,
          },
        },
        state.devices
      ),
    };
  }),

  on(DevicesActions.updateBoilerSerial, (state) => {
    return {
      ...state,
      devices: deviceAdapter.updateOne(
        {
          id: state.selectedDeviceId,
          changes: {
            loading: true,
          },
        },
        state.devices
      ),
    };
  }),

  on(DevicesActions.updateBoilerSerialSuccess, (state, { boilerSerial }) => {
    return {
      ...state,
      devices: deviceAdapter.updateOne(
        {
          id: state.selectedDeviceId,
          changes: {
            loading: false,
            boilerSerial,
          },
        },
        state.devices
      ),
    };
  }),

  on(DevicesActions.updateBoilerSerialError, (state) => {
    return {
      ...state,

      devices: deviceAdapter.updateOne(
        {
          id: state.selectedDeviceId,
          changes: {
            loading: false,
          },
        },
        state.devices
      ),
    };
  }),

  on(
    DevicesActions.loadMaterialDataWhenSerialIsPresent,
    (state, { device }): DevicesState => {
      const deviceToUpdate: RichDevice = Object.values(state.devices.entities).find(
        (dev) => dev.id === device?.id
      );

      if (!deviceToUpdate) {
        return state;
      }

      return {
        ...state,
        devices: deviceAdapter.updateOne(
          {
            id: deviceToUpdate.selectId,
            changes: {
              marketingName: null,
              deviceImage: null,
              shortDeviceName: null,
              canEditBoilerSerial: null,
              maintenancePackageLink: null,
              equipmentNumber: null,
              error: null,
              loading: true,
            },
          },
          state.devices
        ),
      };
    }
  ),

  on(
    DevicesActions.loadMaterialDataWhenSerialIsNotPresent,
    (state, { device }): DevicesState => {
      const deviceToUpdate: RichDevice = Object.values(state.devices.entities).find(
        (dev) => dev.id === device?.id
      );

      if (!deviceToUpdate) {
        return state;
      }

      return {
        ...state,
        devices: deviceAdapter.updateOne(
          {
            id: deviceToUpdate.selectId,
            changes: {
              marketingName: null,
              deviceImage: null,
              shortDeviceName: null,
              canEditBoilerSerial: null,
              maintenancePackageLink: null,
              equipmentNumber: null,
              error: null,
              loading: true,
            },
          },
          state.devices
        ),
      };
    }
  ),

  on(
    DevicesActions.loadBoilerSerialDataSuccess,
    (state, { mainDevicesDetails }): DevicesState => {
      const deviceToUpdate: RichDevice = Object.values(state.devices.entities).find(
        (dev) =>
          dev.gatewaySerial === mainDevicesDetails.gatewaySerial &&
          (!!mainDevicesDetails.boilerSerial && !!dev.boilerSerial
            ? dev.boilerSerial === mainDevicesDetails.boilerSerial
            : mainDevicesDetails.id === dev.id) // In case of solar log
      );

      if (deviceToUpdate == null) {
        return state;
      }

      return {
        ...state,
        devices: deviceAdapter.updateOne(
          {
            id: deviceToUpdate.selectId,
            changes: {
              marketingName: mainDevicesDetails.marketingName,
              gatewayType: mainDevicesDetails.gatewayType,
              deviceImage: mainDevicesDetails.deviceImage,
              shortDeviceName: mainDevicesDetails.shortDeviceName,
              canEditBoilerSerial: mainDevicesDetails.canEditBoilerSerial,
              maintenancePackageLink: mainDevicesDetails.maintenancePackageLink,
              equipmentNumber: mainDevicesDetails.equipmentNumber,
              error: null,
              loading: false,
              isNameLoaded: true,
            },
          },
          state.devices
        ),
      };
    }
  ),

  on(DevicesActions.loadBoilerSerialDataError, (state, { error }) => {
    return {
      ...state,
      devices: deviceAdapter.updateOne(
        {
          id: state.selectedDeviceId,
          changes: {
            marketingName: null,
            deviceImage: null,
            shortDeviceName: null,
            canEditBoilerSerial: null,
            maintenancePackageLink: null,
            equipmentNumber: null,
            error: error ?? null,
            loading: false,
            isNameLoaded: true,
          },
        },
        state.devices
      ),
    };
  }),

  on(
    DevicesActions.loadDiagnosticDataByDevices.success,
    (state, { diagnosticDataArray }): DevicesState => {
      return {
        ...state,
        diagnosticDataArray,
      };
    }
  ),

  on(
    DevicesActions.loadDiagnosticDataByDevices.attempt,
    (state): DevicesState => {
      return {
        ...state,
        diagnosticDataArray: [],
      };
    }
  ),

  on(
    DevicesActions.loadBatteryModuleCategories.success,
    (state, { batteryModuleCategories }): DevicesState => {
      return {
        ...state,
        batteryModuleCategories,
      };
    }
  ),

  on(
    DevicesActions.loadBatteryModuleCategories.error,
    (state): DevicesState => {
      return {
        ...state,
        batteryModuleCategories: [],
      };
    }
  )
);

function updateIsSelectedOfDevices(state: DevicesState, deviceSelectId: string): DevicesState {
  return {
    ...state,
    devices: deviceAdapter.map(
      (device) => ({
        ...device,
        isSelected: device.selectId === deviceSelectId,
      }),
      state.devices
    ),
  };
}
